import { useEffect, useState } from "react";
import { Badge, Button, Card, Field, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { useAdminResource } from "../hooks";
import { ActionNotice } from "../dialogs";
import { ErrorState, LoadingState } from "../components";
import type { SiteConfig } from "../types";

const TEXT_LIMIT = 300;
const APPS = [
  { slug: "nelurio", label: "Nelurio" },
  { slug: "finello", label: "Finello" },
  { slug: "nexdeutsch", label: "NexDeutsch" },
  { slug: "washhalle", label: "Washhalle" }
] as const;

type PricingMap = Record<string, { m: number; y: number }>;

const SURFACES = [
  { label: "nelurio.com", href: "https://nelurio.com/" },
  { label: "Checkout", href: "https://nelurio.com/checkout.html" },
  { label: "app.nelurio.com", href: "https://app.nelurio.com/" },
  { label: "nexdeutsch.nelurio.com", href: "https://nexdeutsch.nelurio.com/" },
  { label: "washhalle.nelurio.com", href: "https://washhalle.nelurio.com/" },
  { label: "admin.nelurio.com", href: "https://admin.nelurio.com/" }
];

export function WebsitePage() {
  const { t, formatDate, formatNumber } = useI18n();
  const config = useAdminResource<SiteConfig>("/site-config");
  // Live per-app status markers (Wix page-status-chip pattern): real
  // subscription counts next to each price card.
  const products = useAdminResource<{ data: Array<{ appSlug: string; premium: number; trials: number; subs: number }> }>("/products");
  const [visible, setVisible] = useState(true);
  const [text, setText] = useState("");
  const [cta, setCta] = useState("");
  const [pricing, setPricing] = useState<PricingMap>({});
  const [seo, setSeo] = useState({ siteTitle: "", siteDescription: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ error?: string; info?: string }>({});
  const statusFor = (slug: string) => (products.data?.data ?? []).find((p) => p.appSlug === slug);
  // Same Intl currency formatting the Overview finance panel uses (locale-aware EUR).
  const eur = (value: number) => formatNumber(value, { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

  // Sync the form whenever fresh server data arrives (initial load and post-save reload).
  useEffect(() => {
    if (config.status !== "ready" || !config.data) return;
    setVisible(Boolean(config.data.topbar?.visible));
    setText(config.data.topbar?.text ?? "");
    setCta(config.data.topbar?.cta ?? "");
    setPricing(config.data.pricing ? JSON.parse(JSON.stringify(config.data.pricing)) : {});
    setSeo({ siteTitle: config.data.seo?.siteTitle ?? "", siteDescription: config.data.seo?.siteDescription ?? "" });
  }, [config.status, config.data]);

  const setPrice = (slug: string, key: "m" | "y", raw: string) => {
    setPricing((current) => ({
      ...current,
      [slug]: { ...current[slug], [key]: raw === "" ? NaN : Number.parseFloat(raw) }
    }));
  };

  const pricingValid = APPS.every(({ slug }) => {
    const price = pricing[slug];
    if (!price) return false;
    return (
      Number.isFinite(price.m) && price.m >= 0.99 && price.m <= 999 &&
      Number.isFinite(price.y) && price.y >= price.m && price.y <= 9999
    );
  });

  const save = async () => {
    setSaving(true);
    setNotice({});
    try {
      await adminApi<SiteConfig>("/site-config", {
        method: "PUT",
        body: JSON.stringify({
          topbar: { visible, text, cta },
          pricing: Object.fromEntries(APPS.map(({ slug }) => [slug, { m: pricing[slug].m, y: pricing[slug].y }])),
          seo: { siteTitle: seo.siteTitle, siteDescription: seo.siteDescription }
        })
      });
      setNotice({ info: t("admin.website.saved") });
      await config.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.website.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.website")} subtitle={t("admin.website.subtitle")} />
      <ActionNotice error={notice.error} info={notice.info} onDismiss={() => setNotice({})} />

      {config.status === "error" && <ErrorState message={config.error} onRetry={() => void config.reload()} />}
      {config.status === "loading" && <LoadingState rows={3} />}

      {config.status === "ready" && config.data && (
        <>
          {/* Prices: the single source of truth for the marketing site, the
              checkout and the finance MRR. */}
          <Card className="admin-form-card">
            <h2 className="admin-form-title">{t("admin.website.pricingTitle")}</h2>
            <p className="admin-form-meta">{t("admin.website.pricingHint")}</p>
            <div className="admin-price-grid">
              {APPS.map(({ slug, label }) => {
                const price = pricing[slug] ?? { m: NaN, y: NaN };
                const valid = Number.isFinite(price.m) && Number.isFinite(price.y) && price.y >= price.m;
                return (
                  <div className="admin-price-card" key={slug}>
                    <div className="admin-price-card__head">
                      <strong>{label}</strong>
                      <Badge tone={valid ? "green" : "amber"}>{valid ? eur(price.m) + t("admin.finance.perMonth") : "—"}</Badge>
                    </div>
                    {statusFor(slug) && (
                      <div className="admin-price-card__status">
                        <Badge tone="purple">{formatNumber(statusFor(slug)!.premium)}× Premium</Badge>
                        {statusFor(slug)!.trials > 0 && <Badge tone="blue">{formatNumber(statusFor(slug)!.trials)}× Trial</Badge>}
                        <a className="admin-price-card__live" href={`https://nelurio.com/checkout.html?app=${slug}`} target="_blank" rel="noreferrer">{t("admin.website.liveLink")}</a>
                      </div>
                    )}
                    <label className="nl-field">
                      <span className="nl-field__label">{t("admin.website.monthlyLabel")}</span>
                      <input
                        className="nl-input"
                        type="number"
                        min={0.99}
                        max={999}
                        step={0.01}
                        inputMode="decimal"
                        aria-label={`${label} monthly`}
                        value={Number.isFinite(price.m) ? price.m : ""}
                        onChange={(event) => setPrice(slug, "m", event.target.value)}
                      />
                    </label>
                    <label className="nl-field">
                      <span className="nl-field__label">{t("admin.website.yearlyLabel")}</span>
                      <input
                        className="nl-input"
                        type="number"
                        min={0.99}
                        max={9999}
                        step={0.01}
                        inputMode="decimal"
                        aria-label={`${label} yearly`}
                        value={Number.isFinite(price.y) ? price.y : ""}
                        onChange={(event) => setPrice(slug, "y", event.target.value)}
                      />
                    </label>
                    {Number.isFinite(price.m) && Number.isFinite(price.y) && price.y < price.m && (
                      <span role="alert" className="nl-field__hint nl-field__hint--error">{t("admin.website.yearlyTooLow")}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="admin-form-actions">
              <Button icon="check" loading={saving} disabled={!pricingValid} onClick={() => void save()}>{t("common.save")}</Button>
              <span className="admin-form-meta">
                {config.data.updatedAt
                  ? config.data.updatedBy
                    ? t("admin.website.lastChangedBy", { at: formatDate(config.data.updatedAt), by: config.data.updatedBy })
                    : t("admin.website.lastChanged", { at: formatDate(config.data.updatedAt) })
                  : t("admin.website.neverSaved")}
              </span>
            </div>
          </Card>

          {/* SEO settings (Wix page-SEO model): site-level title/description
              applied live by the marketing site. */}
          <Card className="admin-form-card">
            <h2 className="admin-form-title">{t("admin.website.seoTitle")}</h2>
            <p className="admin-form-meta">{t("admin.website.seoHint")}</p>
            <Field
              label={t("admin.website.seoTitleLabel")}
              value={seo.siteTitle}
              maxLength={120}
              onChange={(event) => setSeo((current) => ({ ...current, siteTitle: event.target.value }))}
            />
            <Field
              label={t("admin.website.seoDescLabel")}
              value={seo.siteDescription}
              maxLength={300}
              onChange={(event) => setSeo((current) => ({ ...current, siteDescription: event.target.value }))}
            />
          </Card>

          {/* Topbar announcement (unchanged mechanism). */}
          <Card className="admin-form-card">
            <h2 className="admin-form-title">{t("admin.website.topbarTitle")}</h2>
            <label className="admin-check-row">
              <input type="checkbox" checked={visible} onChange={(event) => setVisible(event.target.checked)} />
              <span>{t("admin.website.topbarVisible")}</span>
            </label>

            <Field
              label={t("admin.website.topbarText", { count: text.length, limit: TEXT_LIMIT })}
              value={text}
              maxLength={TEXT_LIMIT}
              hint={t("admin.website.topbarTextHint")}
              onChange={(event) => setText(event.target.value)}
            />
            <Field
              label={t("admin.website.ctaLabel")}
              value={cta}
              placeholder="#pricing"
              hint={t("admin.website.ctaHint")}
              onChange={(event) => setCta(event.target.value)}
            />
          </Card>

          {/* Platform surfaces: direct control links. */}
          <Card className="admin-form-card">
            <h2 className="admin-form-title">{t("admin.website.surfacesTitle")}</h2>
            <div className="admin-surface-grid">
              {SURFACES.map((surface) => (
                <a key={surface.href} className="admin-surface-link" href={surface.href} target="_blank" rel="noreferrer">
                  <span className="admin-surface-link__name">{surface.label}</span>
                  <Icon name="arrow" size={15} />
                </a>
              ))}
            </div>
            <p className="admin-form-meta">{t("admin.website.surfacesHint")}</p>
          </Card>

        </>
      )}
    </div>
  );
}
